import { Trans, useTranslation } from 'react-i18next';

import type {
  MeetingAttendeeNotificationData,
  Notification,
} from '@/api/v4/notifications.api';
import { queryClient, QueryKey } from '@/config/query-client';
import { useActionPanelSearchParams } from '@/features/action-panel/use-action-panel-search-params';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { colors } from '@/theme/colors';
import { Inline } from '@/ui/line/line';
import { SmallText } from '@/ui/typography/widgets';

import {
  NotificationAuthorInitials,
  NotificationCardContainer,
  NotificationHighlight,
} from '../components/notification-card-components';
import { useCommentTagNotificationEntityName } from '../hooks/use-comment-tag-notification-entity-name.query';
import { useMarkNotificationAsRead } from '../hooks/use-mark-notification-as-read.mutation';
import { timeAgo } from '../utils/time-ago';

export const MeetingAttendeeNotificationCard = ({
  notification,
}: {
  notification: Notification;
}) => {
  useTranslation('notification');
  const { addedByName, entityTagged, entityId, meetingId } =
    notification.data as MeetingAttendeeNotificationData;
  const isRead = Boolean(notification?.readAt);
  const entityName = useCommentTagNotificationEntityName({
    entityId,
    entityType: entityTagged,
  });
  const navigateTo = useNavigateTo();
  const { markAsRead } = useMarkNotificationAsRead();
  const { generateActionItemSearchParam } = useActionPanelSearchParams();

  const handleNavigateToMeeting = async () => {
    if (!isRead) {
      await markAsRead({ notificationId: notification.id });
    }

    const meetingSearchParams = generateActionItemSearchParam(
      meetingId,
      'meeting',
    );

    if (entityTagged === 'company') {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.CompanyMeetings, entityId],
      });
      return navigateTo.company({ companyId: entityId }, meetingSearchParams);
    }

    if (entityTagged === 'project') {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.ProjectMeetings, entityId],
      });
      return navigateTo.project({ projectId: entityId }, meetingSearchParams);
    }
  };

  return (
    <NotificationCardContainer
      isRead={isRead}
      justify="space-between"
      alignItems="center"
      onClick={handleNavigateToMeeting}
    >
      <Inline gap="12px" alignItems="center">
        <NotificationAuthorInitials
          name={addedByName ?? ''}
          bgColor={isRead ? colors.system.disable : colors.blue.primaryA}
        />
        <SmallText color={colors.gray.c13}>
          <Trans
            i18nKey="meetingAttendeeAdded"
            ns="notification"
            values={{
              addedByName: addedByName ?? '',
              entityName: entityName ?? '',
            }}
            components={{ strong: <NotificationHighlight /> }}
          />
        </SmallText>
      </Inline>
      <SmallText>{timeAgo(notification?.createdAt)}</SmallText>
    </NotificationCardContainer>
  );
};
